"use client"
import * as React from "react"
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Input } from "../../ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table"
import { DataTablePagination } from "./data-table-pagination"
import { DataTableViewOptions } from "./data-table-view-options"
import { useState, useEffect } from "react"
import { Button } from "../button"
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  searchColumn?: string
  searchPlaceholder?: string
  initialPageSize?: number
  showPagination?: boolean
  showRowsPerPage?: boolean
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchColumn,
  searchPlaceholder = "Search...",
  initialPageSize = 5,
  showPagination = true,
  showRowsPerPage = true,
  showTotal = false, // Enable totals row
  totalColumns = [], // New prop to specify which columns to total
}: DataTableProps<TData, TValue> & { showTotal?: boolean; totalColumns?: string[] }) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [isFilterModalOpen, setFilterModalOpen] = useState(false)

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    initialState: {
      pagination: {
        pageSize: initialPageSize,
      },
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFilterModalOpen) {
        setFilterModalOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isFilterModalOpen])

  // Clear all filters function
  const clearAllFilters = () => {
    table.resetColumnFilters()
  }

  // Get active filter count
  const activeFiltersCount = columnFilters.length

  // Identify numeric columns
  const numericColumns = table.getAllLeafColumns().filter(
    col => col.getCanFilter() && (col.columnDef as any).isNumber
  );

  // Identify the search column
  const searchCol = searchColumn
    ? table.getAllLeafColumns().find(col => col.id === searchColumn && col.getCanFilter())
    : table.getAllLeafColumns().find(col => col.getCanFilter());

  // Calculate totals for specified columns
  const totals = React.useMemo(() => {
    if (!showTotal || totalColumns.length === 0) return null;

    const totalsMap: Record<string, number> = {};
    totalColumns.forEach((colId) => {
      totalsMap[colId] = data.reduce((sum, row) => {
        const value = row[colId as keyof TData];
        return sum + (typeof value === "number" ? value : 0);
      }, 0);
    });

    return totalsMap;
  }, [showTotal, totalColumns, data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Show filter button only if numeric columns exist */}
          {numericColumns.length > 0 && (
            <Button
              variant={activeFiltersCount > 0 ? "default" : "outline"}
              onClick={() => setFilterModalOpen(true)}
            >
              <svg 
                className="w-4 h-4 mr-2 inline-block" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
              Filters
              {activeFiltersCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          )}
          {searchCol && (
            <Input
              className="ml-2 w-56"
              placeholder={searchPlaceholder}
              value={(searchCol.getFilterValue() as string) ?? ""}
              onChange={e => searchCol.setFilterValue(e.target.value)}
            />
          )}
        </div>
        <DataTableViewOptions table={table} />
      </div>

      {/* Enhanced Filter Modal */}
      {isFilterModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in-0"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setFilterModalOpen(false)
            }
          }}
        >
          <div className="bg-gradient-to-br from-background to-muted/30 rounded-xl border border-border/60 shadow-xl p-6 min-w-[400px] max-w-[90vw] max-h-[80vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-2">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-card-foreground">Filter Data</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Refine your search results
                </p>
              </div>
              <button
                className="text-muted-foreground hover:text-card-foreground transition-colors duration-200 p-1 rounded-lg hover:bg-muted/20"
                onClick={() => setFilterModalOpen(false)}
                aria-label="Close filters"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {(() => {
                const numberCols = table.getAllLeafColumns()
                  .filter(col => col.getCanFilter() && (col.columnDef as any).isNumber);

                if (numberCols.length > 0) {
                  return (
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-card-foreground border-b border-border/40 pb-2">
                        Numeric Ranges
                      </h3>
                      {numberCols.map((col) => {
                        const filterValue = col.getFilterValue() as [number?, number?] | undefined || [undefined, undefined];
                        const hasFilter = filterValue[0] !== undefined || filterValue[1] !== undefined;
                        
                        return (
                          <div key={col.id} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="text-sm text-card-foreground font-medium">
                                {typeof col.columnDef.header === "string"
                                  ? col.columnDef.header
                                  : col.id}
                              </label>
                              {hasFilter && (
                                <button
                                  className="text-xs text-muted-foreground hover:text-card-foreground"
                                  onClick={() => col.setFilterValue(undefined)}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="flex-1">
                                <input
                                  type="number"
                                  placeholder="Min"
                                  value={filterValue[0] ?? ""}
                                  onChange={e => {
                                    const val = e.target.value ? Number(e.target.value) : undefined;
                                    col.setFilterValue([val, filterValue[1]]);
                                  }}
                                  className="w-full border border-border/60 rounded-lg px-3 py-2 bg-background text-card-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
                                />
                              </div>
                              <span className="text-muted-foreground font-medium">to</span>
                              <div className="flex-1">
                                <input
                                  type="number"
                                  placeholder="Max"
                                  value={filterValue[1] ?? ""}
                                  onChange={e => {
                                    const val = e.target.value ? Number(e.target.value) : undefined;
                                    col.setFilterValue([filterValue[0], val]);
                                  }}
                                  className="w-full border border-border/60 rounded-lg px-3 py-2 bg-background text-card-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                const firstFilterable = table.getAllLeafColumns().find(col => col.getCanFilter());
                if (firstFilterable) {
                  const hasTextFilter = (firstFilterable.getFilterValue() as string) !== "";
                  
                  return (
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium text-card-foreground border-b border-border/40 pb-2">
                        Text Search
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-card-foreground font-medium">
                            {firstFilterable.columnDef.header as string}
                          </label>
                          {hasTextFilter && (
                            <button
                              className="text-xs text-muted-foreground hover:text-card-foreground"
                              onClick={() => firstFilterable.setFilterValue("")}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <div className="relative">
                          <svg 
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <Input
                            key={firstFilterable.id}
                            placeholder={`Search ${firstFilterable.columnDef.header as string}...`}
                            value={(firstFilterable.getFilterValue() as string) ?? ""}
                            onChange={(event) => firstFilterable.setFilterValue(event.target.value)}
                            className="pl-10 border border-border/60 bg-background text-card-foreground focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="text-center py-8">
                    <div className="text-muted-foreground">
                      <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                      </svg>
                      <p>No filterable columns available</p>
                    </div>
                  </div>
                );
              })()}
            </div>
            
            <div className="flex justify-between items-center mt-8 pt-4 border-t border-border/40">
              <button
                className="px-4 py-2 text-sm text-muted-foreground hover:text-card-foreground transition-colors duration-200"
                onClick={clearAllFilters}
                disabled={activeFiltersCount === 0}
              >
                Clear All Filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-md border">
        <div className="relative w-full overflow-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const isSorted = header.column.getIsSorted();
                    
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : (
                          canSort ? (
                            <div
                              className="flex items-center gap-2 cursor-pointer select-none hover:text-foreground"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {isSorted === "asc" && <ArrowUp className="h-4 w-4" />}
                              {isSorted === "desc" && <ArrowDown className="h-4 w-4" />}
                              {!isSorted && <ArrowUpDown className="h-4 w-4 opacity-50" />}
                            </div>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
              {/* Add totals row */}
              {showTotal && totals && (
                <TableRow>
                  {table.getAllLeafColumns().map((col, index) => (
                    <TableCell key={col.id} className="font-bold">
        {index === 0
          ? "Total" // Add "Total" label in the first column
          : totalColumns.includes(col.id)
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(totals[col.id] ?? 0) // Format as USD currency
          : ""}
      </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {showPagination && (
        <DataTablePagination table={table} showRowsPerPage={showRowsPerPage} />
      )}
    </div>
  );
}